import { supabase } from '../../client';
import { CreateOrderItemParams, OrderItem } from '../types/order';
import { runVerifiedKeysetScan } from '@/utils/keysetScan';

/**
 * 새로운 주문 상품을 생성합니다.
 */
export async function create({ orderId, itemName, price, quantity }: CreateOrderItemParams): Promise<void> {
  const { error } = await supabase
    .from('order_item')
    .insert({
      order_id: orderId,
      item_name: itemName,
      price,
      quantity
    });

  if (error) throw error;
}

/**
 * 특정 주문의 모든 상품을 조회합니다.
 */
export async function findByOrderId(orderId: number): Promise<OrderItem[]> {
  const { data, error } = await supabase
    .from('order_item')
    .select('*')
    .eq('order_id', orderId)
    .order('id', { ascending: true });

  if (error) throw error;
  return data;
}

/**
 * 여러 주문의 모든 상품을 한 번에 조회합니다.
 */
export async function findByOrderIds(orderIds: number[]): Promise<OrderItem[]> {
  if (orderIds.length === 0) return [];
  const CHUNK_SIZE = 500;
  const chunks: number[][] = [];
  for (let i = 0; i < orderIds.length; i += CHUNK_SIZE) {
    chunks.push(orderIds.slice(i, i + CHUNK_SIZE));
  }

  const results: OrderItem[] = [];
  for (const chunk of chunks) {
    const { data, error } = await supabase
      .from('order_item')
      .select('*')
      .in('order_id', chunk)
      .order('id', { ascending: true });

    if (error) throw error;
    if (data) results.push(...data);
  }

  return results;
}

/**
 * 여러 주문의 모든 상품을 한 번에 조회하고, count: 'exact'로 청크별 잘림(max_rows)을 감지합니다.
 * 대시보드 닉네임 검색 전용 — 기존 findByOrderIds는 메인 주문 페이지 소비자(findByUserAndDateWithItems)가 있어 무손상 유지.
 */
export async function findByOrderIdsWithPartialCheck(orderIds: number[]): Promise<{ items: OrderItem[]; isPartial: boolean }> {
  if (orderIds.length === 0) return { items: [], isPartial: false };
  const CHUNK_SIZE = 500;
  const chunks: number[][] = [];
  for (let i = 0; i < orderIds.length; i += CHUNK_SIZE) {
    chunks.push(orderIds.slice(i, i + CHUNK_SIZE));
  }

  const results: OrderItem[] = [];
  let isPartial = false;
  for (const chunk of chunks) {
    const { data, count, error } = await supabase
      .from('order_item')
      .select('*', { count: 'exact' })
      .in('order_id', chunk)
      .order('id', { ascending: true });

    if (error) throw error;
    if (data) {
      results.push(...data);
      if (count !== null && data.length !== count) isPartial = true;
    }
  }

  return { items: results, isPartial };
}

/**
 * 주문 상품을 삭제합니다.
 */
export async function deleteById(itemId: number): Promise<void> {
  const { error } = await supabase
    .from('order_item')
    .delete()
    .eq('id', itemId);

  if (error) throw error;
}

/**
 * 특정 주문의 모든 상품을 삭제합니다.
 */
export async function deleteByOrderId(orderId: number): Promise<void> {
  const { error } = await supabase
    .from('order_item')
    .delete()
    .eq('order_id', orderId);

  if (error) throw error;
}

/**
 * 동일 상품 일괄 수정 RPC 호출.
 * 스코프(활성 주문의 order_date와 같은 날짜) + 상태 '미지정' + 원본 (이름, 가격) 매칭을
 * 서버가 행 잠금(FOR UPDATE) 하에 원자적으로 강제한다 — 미지정-only 돈 불변식.
 * 날짜는 서버가 활성 주문에서 도출하므로 클라이언트가 보내지 않는다.
 * 총액 재계산까지 한 트랜잭션 — 부분 실패 없음. 수량 파라미터는 존재하지 않는다(전파 금지).
 */
export async function bulkUpdateSameProduct(params: {
  excludeOrderId: number;
  matchItemName: string;
  matchPrice: number;
  newItemName?: string;
  newPrice?: number;
}): Promise<{ appliedOrderCount: number; appliedItemCount: number }> {
  const { data, error } = await supabase.rpc('bulk_update_same_product_items', {
    p_exclude_order_id: params.excludeOrderId,
    p_match_item_name: params.matchItemName,
    p_match_price: params.matchPrice,
    p_new_item_name: params.newItemName ?? null,
    p_new_price: params.newPrice ?? null,
  });

  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    appliedOrderCount: row?.applied_order_count ?? 0,
    appliedItemCount: row?.applied_item_count ?? 0,
  };
}

/**
 * 주문 상품의 여러 정보를 한번에 수정합니다.
 */
export async function update(itemId: number, data: { item_name?: string; price?: number; quantity?: number }): Promise<void> {
  const { error } = await supabase
    .from('order_item')
    .update(data)
    .eq('id', itemId);

  if (error) throw error;
}
/**
 * 배송정보 상품명 stale 스캔 전용: keyset 페이지네이션 + 무결성 계약 조회.
 *
 * 기존 findByOrderIds는 PostgREST max_rows에서 조용히 잘릴 수 있다(헤비 셀러 하루 5,000행+).
 * 잘린 데이터로 합성하면 "가짜 최신값"으로 정상 배송정보를 깎아먹으므로 verified=false면
 * 호출부는 스캔 실패로 처리해야 한다. 계약·무한루프 가드는 runVerifiedKeysetScan(단일 소스) 참조.
 */
export async function findByOrderIdsForSyncScan(
  orderIds: number[]
): Promise<{ items: OrderItem[]; verified: boolean }> {
  const unique = Array.from(new Set(orderIds));
  if (unique.length === 0) return { items: [], verified: true };

  const CHUNK_SIZE = 200;
  // PostgREST max_rows가 limit보다 작아도 엔진이 빈 페이지까지 계속 요청한다 — 500은 안전값.
  const PAGE_SIZE = 500;
  const results: OrderItem[] = [];
  let verified = true;

  for (let i = 0; i < unique.length; i += CHUNK_SIZE) {
    const chunk = unique.slice(i, i + CHUNK_SIZE);
    const chunkSet = new Set(chunk);

    const { rows, verified: chunkVerified } = await runVerifiedKeysetScan<OrderItem>(
      {
        fetchCount: async () => {
          const { count, error } = await supabase
            .from('order_item')
            .select('id', { count: 'exact', head: true })
            .in('order_id', chunk);
          if (error) throw error;
          return count;
        },
        fetchPage: async (afterId, limit) => {
          const { data, error } = await supabase
            .from('order_item')
            .select('*')
            .in('order_id', chunk)
            .gt('id', afterId)
            .order('id', { ascending: true })
            .limit(limit);
          if (error) throw error;
          return (data ?? []) as OrderItem[];
        },
      },
      {
        pageSize: PAGE_SIZE,
        getId: (row) => row.id,
        isRowExpected: (row) => chunkSet.has(row.order_id),
      }
    );

    results.push(...rows);
    if (!chunkVerified) verified = false;
  }

  return { items: results, verified };
}
